// Hand-authored pixel art: upright runners at 2x density, with a separate articulated arm/weapon rig.
// Palette ramps and mask identities come from the game's existing theme and crew definitions.
import { Texture } from 'pixi.js';
import { canvas, tex, type Look } from './art';
import { K, px, dk, lt, mix } from './hd-kit';
import { CREW } from './crew';
import { detailedBodyCanvas, RD_W, RD_H } from './runner-detail';
import { HELD_GUNS, heldFamily, heldGun, type HeldParts } from './held-guns';
import { npcBodyCanvas, NPC_ARMOUR } from './npc-detail';
import { FACINGS, WEAPON_SHAPES, rigPose, bladeTrail, type Facing, type MotionPose, type WeaponKind, type ArmPose, type Point, type RigPose } from './character-motion';

export const CHAR_W = 40, CHAR_H = 64, CHAR_SCALE = 0.5;
/** On-screen height of a runner in world px (it was 32). The one constant to change: every sprite, arm, weapon and HUD
 *  offset follows. Art is authored at 32 world px (64 art px = 64 device px at 2x), so it is resampled once into
 *  whole pixels (see shrink) rather than drawn at a fractional sprite scale. */
export const CHAR_WORLD_H = 24;
export const CHAR_RATIO = CHAR_WORLD_H / (CHAR_H * CHAR_SCALE);
/** Shrink a canvas by atio into whole pixels: each output pixel takes the most common opaque colour of the source
 *  block it covers (transparent unless half the block is opaque), so thin features survive better than nearest-neighbour. */
function shrink(src: HTMLCanvasElement, ratio: number, evenWidth = false): HTMLCanvasElement {
  if (ratio === 1) return src;
  let w = Math.max(1, Math.round(src.width * ratio)); if (evenWidth && w % 2) w++;
  const h = Math.max(1, Math.round(src.height * ratio)), [c, g] = canvas(w, h);
  const sx = src.width / w, sy = src.height / h, data = src.getContext('2d')!.getImageData(0, 0, src.width, src.height).data;
  const out = g.createImageData(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const x0 = Math.floor(x * sx), x1 = Math.max(x0 + 1, Math.round((x + 1) * sx)), y0 = Math.floor(y * sy), y1 = Math.max(y0 + 1, Math.round((y + 1) * sy));
    const votes = new Map<number, number>(); let solid = 0, total = 0;
    for (let yy = y0; yy < Math.min(y1, src.height); yy++) for (let xx = x0; xx < Math.min(x1, src.width); xx++) {
      total++; const i = (yy * src.width + xx) * 4; if (data[i + 3] < 128) continue;
      solid++; const k = data[i] << 16 | data[i + 1] << 8 | data[i + 2]; votes.set(k, (votes.get(k) ?? 0) + 1);
    }
    if (solid * 2 < total) continue;
    let best = 0, n = 0; for (const [k, v] of votes) if (v > n) { best = k; n = v; }
    const o = (y * w + x) * 4; out.data[o] = best >> 16; out.data[o + 1] = best >> 8 & 255; out.data[o + 2] = best & 255; out.data[o + 3] = 255;
  }
  g.putImageData(out, 0, 0); return c;
}
const CACHE_LIMIT = 768;
type BodyEntry = { canvas: HTMLCanvasElement; texture?: Texture; users: number };
const bodyCache = new Map<string, BodyEntry>();
const textureEntries = new WeakMap<Texture, BodyEntry>();
const weapons = new Map<string, { canvas: HTMLCanvasElement; texture?: Texture }>();

function pruneBodies(protectedKey?: string) {
  for (const [key, entry] of bodyCache) {
    if (bodyCache.size <= CACHE_LIMIT) break;
    // A cached pose can still be displayed by another actor. Never destroy a live sprite's texture.
    if (entry.users || key === protectedKey) continue;
    entry.texture?.destroy(true); bodyCache.delete(key);
  }
}
export function retainBodyTexture(t: Texture) { const entry = textureEntries.get(t); if (entry) entry.users++; }
export function releaseBodyTexture(t: Texture) { const entry = textureEntries.get(t); if (entry) entry.users = Math.max(0, entry.users - 1); pruneBodies(); }

/** Solid scanlines rather than anti-aliased polygon edges. */
function polygon(g: CanvasRenderingContext2D, points: number[][], color: string) {
  const minY = Math.ceil(Math.min(...points.map(p => p[1]))), maxY = Math.ceil(Math.max(...points.map(p => p[1])));
  for (let y = minY; y < maxY; y++) {
    const cuts: number[] = [];
    for (let i = 0; i < points.length; i++) {
      const p = points[i], q = points[(i + 1) % points.length];
      if ((p[1] <= y + 0.5 && q[1] > y + 0.5) || (q[1] <= y + 0.5 && p[1] > y + 0.5)) cuts.push(p[0] + (y + 0.5 - p[1]) / (q[1] - p[1]) * (q[0] - p[0]));
    }
    cuts.sort((a, b) => a - b);
    for (let i = 0; i + 1 < cuts.length; i += 2) { const x = Math.round(cuts[i]); px(g, x, y, Math.round(cuts[i + 1]) - x, 1, color); }
  }
}
function outlined(src: HTMLCanvasElement): HTMLCanvasElement {
  const [c, g] = canvas(src.width, src.height), [mask, mg] = canvas(src.width, src.height);
  mg.drawImage(src, 0, 0); mg.globalCompositeOperation = 'source-in'; mg.fillStyle = K.ink; mg.fillRect(0, 0, src.width, src.height);
  for (const [x, y] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) g.drawImage(mask, x, y);
  g.drawImage(src, 0, 0); return c;
}
/** Pixel-grid thick segment, shared by cloth limbs and the animated sleeves. */
function pixelSegment(g: CanvasRenderingContext2D, a: Point, b: Point, width: number, color: string) {
  const dx = b.x - a.x, dy = b.y - a.y, steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy))));
  const w = Math.max(1, Math.round(width));
  for (let i = 0; i <= steps; i++) px(g, Math.round(a.x + dx * i / steps - w / 2), Math.round(a.y + dy * i / steps - w / 2), w, w, color);
}
function bodyKey(l: Look, p: MotionPose, r: number) { return `${r}|${l.skin}|${l.jacket}|${l.hair}|${l.extra}|${l.crew}|${l.npc}|${l.v}|${p.facing}|${p.travel}|${p.frame}|${p.frame ? 0 : p.idle === 2 ? 1 : 0}|${p.down}`; }

export function characterBodyCanvas(l: Look, p: MotionPose, ratio = CHAR_RATIO): HTMLCanvasElement {
  const key = bodyKey(l, p, ratio), hit = bodyCache.get(key);
  if (hit) { bodyCache.delete(key); bodyCache.set(key, hit); return hit.canvas; }
  if (ratio === CHAR_RATIO) {
    // game size: painted directly at 30x48 (runner-detail.ts), so 1 px details aren't voted away by shrink (owner, 2026-10-12)
    const extra = p.down ? (dg: CanvasRenderingContext2D) => {
      const rest = rigPose({ ...p, kind: 'none', throwing: -1, swing: -1, reload: -1 });
      dg.save(); dg.translate(RD_W / 2, RD_H); drawArmCanvas(dg, rest.left, l, ratio); drawArmCanvas(dg, rest.right, l, ratio); dg.restore();
    } : undefined;
    const out = (l.npc ? npcBodyCanvas : detailedBodyCanvas)(l, p.facing, { bob: Math.round(p.bob * 1.5), frame: p.frame, travel: FACINGS.indexOf(p.travel) * Math.PI / 4, extra });
    bodyCache.set(key, { canvas: out, users: 0 }); pruneBodies(key); return out;
  }
  const [c, g] = canvas(CHAR_W, CHAR_H);
  const view = p.facing, west = view === 'W' || view === 'NW' || view === 'SW';
  const profile = view === 'E' || view === 'W', back = view === 'N' || view === 'NE' || view === 'NW';
  const diagonal = !profile && view !== 'S' && view !== 'N', direction = west ? -1 : 1;
  const crew = l.crew, mark = crew !== undefined ? (CREW[crew]?.color ?? CREW[0].color) : lt(l.jacket.slice(0, 7), 0.25);
  const jacket = l.jacket.slice(0, 7), hi = lt(jacket, 0.28), lo = dk(jacket, 0.38), deep = dk(jacket, 0.7);
  const metal = lt(K.slate, 0.26), pants = mix(dk(jacket, 0.62), K.slate, 0.35), bob = p.bob * 2;
  const cx = CHAR_W / 2, lean = profile || diagonal ? direction : 0;
  const cycle = p.frame ? (p.frame - 1) / 8 * Math.PI * 2 : 0;
  const travelAngle = FACINGS.indexOf(p.travel) * Math.PI / 4;
  const broad = crew === 1 || l.extra === 'bulky' || l.extra === 'helmet';
  const torsoW = profile ? 11 : broad ? 19 : diagonal ? 15 : 17, tx = cx - Math.floor(torsoW / 2) + lean;

  // Foot placement follows movement, independently of where the upper body aims. Two planted-foot contacts per cycle.
  const leg = (side: number, far: boolean) => {
    const phase = cycle + (side < 0 ? Math.PI : 0), stride = p.frame ? Math.sin(phase) : 0;
    const lift = p.frame ? Math.max(0, Math.cos(phase)) * 3 : 0;
    const hip = { x: cx + (profile ? side * 1.5 : side * 3.5), y: 43 + bob / 2 };
    const foot = { x: cx + (profile ? side * 1.5 : side * 4) + Math.cos(travelAngle) * stride * 5,
      y: 61 + Math.sin(travelAngle) * stride * 2 - lift };
    const knee = { x: hip.x + (foot.x - hip.x) * 0.5 + side * 0.8, y: 52 - lift * 0.35 };
    const col = far ? dk(pants, 0.2) : pants;
    pixelSegment(g, hip, knee, 5, col); pixelSegment(g, knee, { x: foot.x, y: foot.y - 2 }, 4, col);
    px(g, Math.round(knee.x - 2), Math.round(knee.y - 2), 4, 4, far ? dk(K.metal, 0.3) : K.metal);
    px(g, Math.round(knee.x - 2), Math.round(knee.y - 2), 3, 1, metal);
    // cargo-pocket seam, shin strip and a three-tone reinforced boot
    px(g, Math.round(hip.x - 2), 46 + Math.round(bob / 2), 3, 2, dk(col, 0.15));
    px(g, Math.round(knee.x - 1), Math.round(knee.y + 3), 1, 3, lt(col, 0.22));
    const fx = Math.round(foot.x - 3), fy = Math.round(foot.y - 4);
    px(g, fx, fy, 6, 4, K.ink); px(g, fx + 1, fy, 4, 2, dk(K.slate, 0.18));
    px(g, fx + 1, fy + 1, 3, 1, metal); px(g, fx - (profile && west ? 1 : 0), fy + 3, 7, 1, dk(K.slate, 0.05));
  };
  leg(profile ? -direction : -1, true); leg(profile ? direction : 1, false);
  px(g, tx + 2, 41 + Math.round(bob / 2), torsoW - 4, 6, pants);

  // Karasu's split coat tails; the other runners have a compact tactical jacket.
  if (crew === 3) {
    polygon(g, [[tx, 37 + bob], [cx - 1, 38 + bob], [cx - 2 + Math.round(Math.sin(cycle)), 51], [tx - 1, 48]], deep);
    polygon(g, [[cx + 1, 38 + bob], [tx + torsoW, 37 + bob], [tx + torsoW + 1, 48], [cx + 2 + Math.round(Math.sin(cycle)), 51]], lo);
    px(g, tx, 47, 1, 2, dk(mark, 0.2)); px(g, tx + torsoW, 47, 1, 2, dk(mark, 0.2));
  }
  // Shoulder silhouette, collar, chest planes, elbow-side side seam and panelled hem.
  polygon(g, [[tx + 3, 23 + bob], [tx + torsoW - 3, 23 + bob], [tx + torsoW + 1, 27 + bob], [tx + torsoW, 42 + bob], [tx, 42 + bob], [tx - 1, 27 + bob]], jacket);
  px(g, tx, 27 + bob, 2, 13, hi); px(g, tx + torsoW - 2, 27 + bob, 2, 15, lo);
  px(g, tx + 2, 25 + bob, torsoW - 4, 2, hi); px(g, tx + 2, 40 + bob, torsoW - 4, 2, deep);
  polygon(g, [[cx - 5 + lean, 22 + bob], [cx + 5 + lean, 22 + bob], [cx + 4 + lean, 27 + bob], [cx + lean, 29 + bob], [cx - 4 + lean, 27 + bob]], deep);
  px(g, cx - 2 + lean, 21 + bob, 4, 3, dk(l.skin, 0.25));
  if (back) {
    px(g, tx + 3, 29 + bob, torsoW - 6, 8, lo); px(g, tx + 3, 29 + bob, torsoW - 6, 1, hi);
    // A small geometric crew badge, not invented Japanese lettering.
    polygon(g, [[cx + lean, 29 + bob], [cx + 3 + lean, 32 + bob], [cx + lean, 35 + bob], [cx - 3 + lean, 32 + bob]], dk(mark, 0.12));
    px(g, cx + lean, 31 + bob, 1, 3, lt(mark, 0.45)); px(g, tx + torsoW - 4, 36 + bob, 1, 3, hi);
  } else {
    const zip = cx + (diagonal ? direction * 3 : profile ? direction * 4 : 0);
    px(g, zip, 27 + bob, 1, 13, deep); px(g, zip + 1, 28 + bob, 1, 10, metal); px(g, zip, 28 + bob, 2, 1, K.white);
    if (!profile) {
      px(g, tx + 3, 30 + bob, 4, 4, lo); px(g, tx + 3, 30 + bob, 4, 1, hi); px(g, tx + 5, 32 + bob, 1, 1, metal);
      px(g, tx + torsoW - 6, 30 + bob, 3, 5, deep); px(g, tx + torsoW - 6, 30 + bob, 3, 1, mark);
      pixelSegment(g, { x: tx + torsoW - 4, y: 27 + bob }, { x: tx + 4, y: 39 + bob }, 2, deep);
      px(g, cx + 1, 34 + bob, 2, 2, metal);
    }
  }
  // Edge lighting stays narrow so the crew reads against the neon signs.
  px(g, tx + 1, 26 + bob, 1, 8, dk(mark, 0.18)); px(g, tx + torsoW - 2, 26 + bob, 1, 4, dk(mark, 0.32));
  px(g, tx + 1, 41 + bob, torsoW - 2, 1, lo);
  px(g, tx + 1, 42 + bob, torsoW - 2, 2, K.ink); px(g, cx - 1 + lean, 42 + bob, 3, 2, metal); px(g, cx + lean, 42 + bob, 1, 1, K.white);
  px(g, tx, 42 + bob, 3, 4, deep); px(g, tx + 1, 42 + bob, 2, 1, hi); px(g, tx + torsoW - 3, 42 + bob, 3, 3, lo);
  if (broad) { px(g, tx + 2, 27 + bob, torsoW - 4, 3, K.metal); px(g, tx + 2, 27 + bob, torsoW - 4, 1, metal); px(g, cx - 4, 28 + bob, 1, 1, mark); }

  // Sculpted head/jaw and two hair planes, at the same eye line for all four masks.
  const hx = cx - 6 + lean, hy = 7 + bob;
  polygon(g, [[hx + 2, hy], [hx + 10, hy], [hx + 12, hy + 3], [hx + 12, hy + 11], [hx + 8, hy + 15], [hx + 4, hy + 15], [hx, hy + 11], [hx, hy + 3]], l.skin);
  px(g, hx + 9, hy + 3, 3, 9, dk(l.skin, 0.25)); px(g, hx + 2, hy + 12, 7, 2, dk(l.skin, 0.14));
  polygon(g, [[hx, hy + 4], [hx, hy + 1], [hx + 3, hy - 1], [hx + 9, hy - 1], [hx + 12, hy + 2], [hx + 12, hy + 5], [hx + 9, hy + 3], [hx + 4, hy + 4]], l.hair);
  px(g, hx + 3, hy, 6, 1, lt(l.hair, 0.23)); px(g, hx + 2, hy + 2, 3, 1, lt(l.hair, 0.1));
  if (back) { px(g, hx + 1, hy + 4, 10, 7, l.hair); px(g, hx + 2, hy + 10, 8, 2, dk(l.hair, 0.2)); }
  else if (profile) { px(g, west ? hx + 8 : hx + 1, hy + 4, 3, 7, l.hair); px(g, west ? hx + 2 : hx + 9, hy + 7, 1, 2, K.ink); }
  else { px(g, hx, hy + 4, 2, 6, l.hair); px(g, hx + 11, hy + 4, 1, 5, l.hair); px(g, hx + 3, hy + 7, 2, 1, K.ink); px(g, hx + 8, hy + 7, 2, 1, K.ink); px(g, hx + 5, hy + 11, 3, 1, dk(l.skin, 0.45)); }
  drawHeadGear(g, l, view, hx, hy);
  if (p.down) {
    const rest = rigPose({ ...p, kind: 'none', throwing: -1, swing: -1, reload: -1 });
    g.save(); g.translate(CHAR_W / 2, CHAR_H); drawArmCanvas(g, rest.left, l); drawArmCanvas(g, rest.right, l); g.restore();
  }

  const out = outlined(shrink(c, ratio, true)); bodyCache.set(key, { canvas: out, users: 0 }); pruneBodies(key);
  return out;
}
export function characterBodyTexture(l: Look, p: MotionPose): Texture {
  const c = characterBodyCanvas(l, p), entry = bodyCache.get(bodyKey(l, p, CHAR_RATIO))!;
  if (!entry.texture) { entry.texture = tex(c); textureEntries.set(entry.texture, entry); }
  return entry.texture;
}
function drawHeadGear(g: CanvasRenderingContext2D, l: Look, view: Facing, hx: number, hy: number) {
  const back = view === 'N' || view === 'NE' || view === 'NW', side = view === 'E' || view === 'W';
  const west = view === 'W' || view === 'SW' || view === 'NW', shift = side ? (west ? 0 : 5) : view === 'SE' ? 2 : view === 'SW' ? -1 : 0;
  if (l.extra === 'cap') { px(g, hx - 1, hy - 1, 14, 4, mix(K.night, K.cyan, 0.23)); px(g, hx + 2, hy - 1, 8, 1, K.metal); if (!back) px(g, hx + shift - 1, hy + 3, side ? 9 : 14, 1, K.ink); }
  if (l.extra === 'helmet') { px(g, hx - 1, hy - 1, 14, 9, K.metalLo); px(g, hx, hy, 11, 2, K.metalHi); if (!back) px(g, hx + shift, hy + 6, side ? 7 : 12, 3, dk(K.amber, 0.2)); }
  if (l.extra === 'visor' && !back) { px(g, hx + shift, hy + 6, side ? 7 : 12, 3, dk(K.pink, 0.35)); px(g, hx + shift + 1, hy + 6, side ? 5 : 9, 1, lt(K.pink, 0.4)); }
  if (l.extra === 'mask' && !back) { px(g, hx + shift, hy + 7, side ? 7 : 12, 6, K.white); px(g, hx + shift + 2, hy + 9, 2, 2, K.pink); if (!side) px(g, hx + 8, hy + 9, 2, 2, K.pink); }
  if (l.crew === undefined) return;
  const crew = l.crew, mark = CREW[crew]?.color ?? CREW[0].color, shell = lt(K.slate, 0.08), hi = lt(shell, 0.19), lo = dk(shell, 0.38);
  if (crew === 1 || crew === 2) {
    for (const ex of [hx, hx + 10]) {
      polygon(g, [[ex, hy + 3], [ex - (crew === 1 ? 1 : 0), hy - 4], [ex + 3, hy - 1], [ex + 3, hy + 3]], shell);
      px(g, ex, hy - 3, 1, 3, crew === 1 ? mark : hi);
    }
  }
  if (back) { px(g, hx - 1, hy + 8, 14, 2, lo); px(g, hx + 10, hy + 7, 2, 3, hi); px(g, hx + 4, hy + 8, 3, 1, dk(mark, 0.35)); return; }
  const mx = hx + shift, mw = side ? 7 : 12;
  polygon(g, [[mx + 1, hy + 3], [mx + mw - 1, hy + 3], [mx + mw, hy + 5], [mx + mw - 1, hy + 12], [mx + Math.floor(mw / 2), hy + 15], [mx + 1, hy + 12], [mx, hy + 5]], shell);
  px(g, mx + 1, hy + 4, mw - 2, 1, hi); px(g, mx + mw - 2, hy + 6, 1, 6, lo);
  if (side) {
    const ex = mx + (west ? 1 : 3); px(g, ex, hy + 6, 3, 1, mark); px(g, ex + 1, hy + 7, 2, 1, K.white);
    px(g, ex, hy + 10, 2, 1, mark);
    if (crew === 1) px(g, ex + 1, hy + 11, 1, 2, K.white);
    if (crew === 3) { const bx = west ? mx - 4 : mx + 6; polygon(g, [[bx, hy + 8], [bx + 6, hy + 9], [bx + (west ? 0 : 5), hy + 13]], lo); px(g, bx + 1, hy + 10, 4, 1, mark); }
    return;
  }
  // Shared white eyes and inset crew-colour LED marks.
  px(g, mx + 1, hy + 6, 4, 1, mark); px(g, mx + 7, hy + 6, 4, 1, mark);
  px(g, mx + 2, hy + 7, 3, 1, K.white); px(g, mx + 7, hy + 7, 3, 1, K.white);
  if (crew === 0) { px(g, mx + 1, hy + 4, 1, 2, mark); px(g, mx + 10, hy + 4, 1, 2, mark); px(g, mx + 2, hy + 10, 2, 1, mark); px(g, mx + 8, hy + 10, 2, 1, mark); px(g, mx + 4, hy + 12, 4, 1, mark); }
  if (crew === 1) { px(g, mx + 2, hy + 10, 8, 1, mark); px(g, mx + 3, hy + 11, 1, 2, K.white); px(g, mx + 8, hy + 11, 1, 2, K.white); }
  if (crew === 2) { px(g, mx + 5, hy + 4, 2, 2, mark); px(g, mx + 1, hy + 9, 2, 1, mark); px(g, mx + 9, hy + 9, 2, 1, mark); px(g, mx + 5, hy + 12, 2, 1, mark); }
  if (crew === 3) { polygon(g, [[mx + 4, hy + 8], [mx + 8, hy + 8], [mx + 7, hy + 16], [mx + 5, hy + 16]], lo); px(g, mx + 6, hy + 9, 1, 7, mark); }
}

export interface InkRect { x: number; y: number; w: number; h: number; color: string }
/** The same pixel rectangles feed Canvas previews and the Pixi arm layers. */
export function armRectangles(arm: ArmPose, l: Look, ratio = CHAR_RATIO): InkRect[] {
  const out: InkRect[] = [];
  const q = (v: number) => Math.max(1, Math.round(v * ratio));
  const rect = (x: number, y: number, w: number, h: number, color: string) => out.push({ x: Math.round(x), y: Math.round(y), w: ratio === 1 ? w : q(w), h: ratio === 1 ? h : q(h), color });
  const segment = (a: Point, b: Point, width: number, color: string) => {
    const steps = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
    for (let i = 0; i <= steps; i++) out.push({ x: Math.round(a.x + (b.x - a.x) * i / steps - width / 2), y: Math.round(a.y + (b.y - a.y) * i / steps - width / 2), w: width, h: width, color });
  };
  const source = (p: Point): Point => ({ x: p.x * 2 * ratio, y: p.y * 2 * ratio });
  const s = source(arm.shoulder), e = source(arm.elbow), h = source(arm.hand), jacket = l.jacket.slice(0, 7);
  const upper = q(5), fore = q(4); // the ink edge stays one pixel a side
  segment(s, e, upper + 2, K.ink); segment(e, h, fore + 2, K.ink);
  segment(s, e, upper, arm.near ? lt(jacket, 0.17) : dk(jacket, 0.28));
  segment(e, h, fore, arm.near ? jacket : dk(jacket, 0.38));
  // runners and armoured NPCs have elbow pads and cuffed gloves; other NPCs a plain sleeve and a bare hand
  const armour = !l.npc || NPC_ARMOUR.has(l.npc);
  if (armour) { rect(e.x - 2, e.y - 2, 4, 3, K.metalLo); rect(e.x - 1, e.y - 2, 2, 1, K.metalHi); }
  rect(h.x - 2, h.y - 2, 4, 4, K.ink); rect(h.x - 1, h.y - 2, 2, 2, lt(l.skin, 0.05));
  rect(h.x - 2, h.y, 4, 1, armour ? K.metal : dk(l.skin, 0.25)); return out;
}
function drawArmCanvas(g: CanvasRenderingContext2D, arm: ArmPose, l: Look, ratio = 1) {
  for (const r of armRectangles(arm, l, ratio)) px(g, r.x, r.y, r.w, r.h, r.color);
}

export function weaponCanvas(kind: Exclude<WeaponKind, 'none'>, crewColor: string = K.cyan, magazineOut = false, ratio = CHAR_RATIO): HTMLCanvasElement {
  const key = `${ratio}|${kind}|${kind === 'tanto' ? crewColor : ''}|${magazineOut}`, hit = weapons.get(key); if (hit) return hit.canvas;
  const shape = WEAPON_SHAPES[kind], [c, g] = canvas(shape.w, shape.h);
  if (kind === 'katana') {
    // Wrapped tsuka, pierced tsuba, a dark spine and a stepped hamon cutting edge.
    px(g, 2, 5, 10, 3, K.ink); px(g, 2, 5, 1, 3, K.amber);
    for(let x=4;x<12;x+=3){px(g,x,5,1,1,K.pink);px(g,x+1,6,1,1,K.metalHi);px(g,x,7,1,1,K.pink);}
    px(g,12,2,2,7,K.warmLo);px(g,12,2,1,7,K.amber);px(g,13,4,1,3,K.ink);
    polygon(g,[[14,4],[44,3],[47,4],[43,7],[14,7]],K.metalHi);
    px(g,15,4,29,1,K.white);px(g,15,6,27,1,K.metal);px(g,14,4,2,3,K.warm);
    for(let x=18;x<42;x+=4){px(g,x,5,2,1,lt(K.cyan,.7));px(g,x+2,6,2,1,K.white);}
    px(g,44,4,2,1,K.white);
  } else if (kind === 'naginata') {
    // Shingan's polearm: a black lacquered shaft with white wraps, a metal ishizuki, an amber tsuba and a long curved
    // blade whose edge carries a thin cyan line (Kanemori steel).
    px(g, 0, 5, 3, 3, K.metalHi); px(g, 0, 7, 3, 1, K.metalLo);
    px(g, 3, 5, 51, 3, K.ink); px(g, 3, 5, 51, 1, mix(K.ink, K.slate, 0.5));
    for (const x of [14, 22, 30, 38]) { px(g, x, 5, 2, 3, K.white); px(g, x + 1, 7, 1, 1, dk(K.white, 0.3)); }
    px(g, 54, 3, 2, 7, K.amber); px(g, 55, 3, 1, 7, dk(K.amber, 0.35));
    polygon(g, [[56, 5], [70, 4], [76, 2], [79, 1], [77, 4], [70, 8], [56, 8]], K.metalHi);
    px(g, 57, 5, 13, 1, K.white); px(g, 70, 4, 5, 1, K.white); px(g, 57, 7, 13, 1, K.metal);
    for (let x = 59; x < 72; x += 3) px(g, x, 6, 2, 1, lt(K.cyan, 0.4));
  } else if (kind === 'tanto') {
    px(g,2,5,6,3,K.ink);for(let x=3;x<8;x+=2){px(g,x,5,1,1,crewColor);px(g,x+1,7,1,1,K.metalHi);}
    px(g,8,3,2,6,K.metalHi);px(g,8,4,1,4,dk(crewColor,.15));
    polygon(g,[[10,4],[21,3],[24,4],[20,7],[10,7]],dk(crewColor,.2));
    px(g,11,4,10,1,crewColor);px(g,11,6,9,1,K.white);px(g,21,4,2,1,K.white);
    px(g,11,5,2,1,K.metalHi);px(g,15,5,4,1,crewColor);
  } else if (kind === 'pistol') {
    polygon(g,[[4,4],[18,3],[21,4],[21,7],[8,7],[8,9],[4,9]],K.metal);
    px(g,5,3,14,1,K.metalHi);px(g,5,4,14,1,lt(K.metal,.1));px(g,19,4,2,3,K.ink);
    for(let x=6;x<11;x+=2)px(g,x,4,1,2,K.ink);
    px(g,12,4,3,2,K.ink);px(g,12,4,2,1,K.metalHi);px(g,17,2,1,2,K.cyan);
    polygon(g,[[5,7],[10,7],[8,12],[4,12]],K.ink);px(g,5,8,3,3,K.metalLo);
    px(g,6,8,1,1,K.metalHi);px(g,5,10,2,1,K.metal);px(g,10,8,5,3,K.ink);px(g,11,8,3,2,K.night);
    px(g,6,2,3,1,K.metalLo);px(g,7,2,1,1,K.pink);px(g,18,7,2,2,K.metalLo);
  } else {
    const x=shape.grip.x, end=shape.muzzle.x, rifle=kind==='rifle', shot=kind==='shotgun', coil=kind==='coilgun', smg=kind==='smg';
    const furniture=rifle?mix(K.amber,K.slate,.5):K.metalLo;
    polygon(g,[[2,4],[x-4,5],[x-3,8],[2,10]],furniture);px(g,1,4,2,6,K.ink);px(g,3,5,x-7,1,lt(furniture,.35));
    if(smg){px(g,3,6,4,2,K.night);px(g,2,8,5,1,K.metalHi);}
    if(shot){px(g,3,6,6,2,K.ink);px(g,3,9,5,1,K.metalHi);}
    polygon(g,[[x-3,4],[x+9,3],[x+12,5],[x+11,8],[x-2,9]],K.metal);
    px(g,x-2,4,11,1,K.metalHi);px(g,x+2,5,5,2,K.ink);px(g,x+3,5,3,1,K.metalHi);px(g,x+7,6,2,1,K.white);
    px(g,x-1,9,3,4,K.ink);px(g,x,9,1,2,K.metalHi);px(g,x+3,8,3,3,K.ink);px(g,x+4,8,1,2,K.night);
    const fore=x+10, len=end-fore-4;
    px(g,fore,4,len,5,furniture);px(g,fore,4,len,1,lt(furniture,.35));
    for(let i=fore+1;i<fore+len-1;i+=3){px(g,i,5,1,2,K.ink);px(g,i+1,7,1,1,K.metalHi);}
    px(g,fore+len,5,5,2,K.metalHi);px(g,end-2,4,3,4,K.ink);px(g,end-2,4,2,1,K.metalHi);
    px(g,x+2,2,7,2,K.ink);px(g,x+3,2,5,1,K.metalHi);px(g,x+7,2,1,1,K.cyan);
    if(rifle){px(g,fore+2,3,len-4,1,K.ink);px(g,x-3,6,2,1,K.metalHi);px(g,4,7,5,1,dk(furniture,.3));}
    if(shot){
      px(g,fore,8,len+2,2,K.metalLo);px(g,fore+1,8,len,1,K.metalHi);
      px(g,x+3,1,5,2,K.ink);px(g,x+4,1,3,1,K.red);px(g,end-1,5,1,2,K.metalLo);
    }
    if(!magazineOut){
      if(shot){polygon(g,[[x+5,8],[x+11,8],[x+14,10],[x+14,13],[x+11,15],[x+6,14],[x+4,11]],K.ink);px(g,x+6,9,6,4,K.metalLo);px(g,x+7,9,4,1,K.metalHi);px(g,x+8,10,2,3,K.metal);}
      else if(smg){px(g,x+5,8,4,6,K.ink);px(g,x+6,9,2,4,K.metalLo);px(g,x+6,10,1,2,K.metalHi);}
      else{polygon(g,[[x+5,8],[x+9,8],[x+11,13],[x+7,14]],K.ink);px(g,x+6,9,3,2,K.metalLo);px(g,x+7,11,2,2,K.metalHi);}
    }
    if(coil){
      px(g,fore,3,len,7,K.ink);px(g,fore+1,4,len-2,5,K.metalLo);
      for(let i=fore+1;i<fore+len;i+=3){px(g,i,3,2,7,dk(K.cyan,.3));px(g,i,4,1,5,K.cyan);px(g,i,4,1,1,K.white);}
      px(g,fore-3,5,2,3,K.cyan);px(g,end-3,3,4,6,K.metalLo);px(g,end-1,4,1,4,K.cyan);
    }
    if(smg){px(g,x+7,5,3,1,K.cyan);px(g,fore+1,8,2,3,K.metalLo);}
  }
  const out = outlined(shrink(c, ratio)); weapons.set(key, { canvas: out }); return out;
}
export function weaponTexture(kind: Exclude<WeaponKind, 'none'>, color: string = K.cyan, magazineOut = false): Texture {
  const c = weaponCanvas(kind, color, magazineOut), entry = weapons.get(`${CHAR_RATIO}|${kind}|${kind === 'tanto' ? color : ''}|${magazineOut}`)!; return entry.texture ??= tex(c);
}

/** Exact same body art, joint solve, grips and action timing as the production rig. Used by the review sheet/video. */
export function drawCharacterCanvas(g: CanvasRenderingContext2D, l: Look, p: MotionPose, rig: RigPose, ratio = 1,
  gun: { id?: string; parts?: HeldParts; legacy?: boolean } = {}) {
  const body = () => { const c = characterBodyCanvas(l, p, ratio); g.drawImage(c, -c.width / 2, -c.height); };
  const arm = (a: ArmPose) => drawArmCanvas(g, a, l, ratio);
  const weaponId = gun.id ?? '', parts = gun.parts;
  const family = HELD_GUNS && !gun.legacy && ratio === CHAR_RATIO && rig.kind !== 'none' ? heldFamily(weaponId, rig.kind) : undefined;
  const magOut = p.reload >= 0.22 && p.reload < 0.67;
  const weapon = () => {
    if (!rig.weaponVisible || rig.kind === 'none') return;
    g.save(); g.translate(rig.grip.x * 2 * ratio, rig.grip.y * 2 * ratio); g.rotate(rig.angle);
    if (family) {
      const held = heldGun(family, magOut, parts);
      if (Math.cos(rig.angle) < 0) g.scale(1, -1); // sights stay on top when aiming west
      g.drawImage(held.canvas, -held.grip.x, -held.grip.y); g.restore(); return;
    }
    const w = WEAPON_SHAPES[rig.kind];
    g.drawImage(weaponCanvas(rig.kind, CREW[l.crew ?? 0]?.color ?? K.cyan, p.reload >= 0.22 && p.reload < 0.67, ratio), -w.grip.x * ratio, -w.grip.y * ratio); g.restore();
  };
  const far = rig.right.near ? rig.left : rig.right, near = rig.right.near ? rig.right : rig.left;
  if (p.down) { g.save(); g.translate(0, -15 * ratio); g.rotate(Math.PI / 2); g.drawImage(characterBodyCanvas(l, p, ratio), -CHAR_W * ratio / 2, -CHAR_H * ratio / 2); g.restore(); return; }
  arm(far); if (rig.weaponBack) weapon(); body(); if (!rig.weaponBack) weapon(); arm(near);
  const trail = bladeTrail(p), tint = rig.kind === 'tanto' ? CREW[l.crew ?? 0]?.color ?? K.cyan : K.cyan;
  for (let i = 1; i < trail.length; i++) {
    const a = { x: trail[i - 1].x * 2 * ratio, y: trail[i - 1].y * 2 * ratio }, b = { x: trail[i].x * 2 * ratio, y: trail[i].y * 2 * ratio };
    pixelSegment(g, a, b, 3, dk(tint, 0.35)); pixelSegment(g, a, b, 1, K.white);
  }
  if (rig.magazine) { px(g, Math.round(rig.magazine.x * 2 * ratio - 2), Math.round(rig.magazine.y * 2 * ratio), 4, 6, K.metalLo); px(g, Math.round(rig.magazine.x * 2 * ratio - 2), Math.round(rig.magazine.y * 2 * ratio), 1, 5, K.metalHi); }
  if (rig.flash && rig.weaponVisible) {
    g.save();
    if (family) { // the flash sits on the held sprite's own muzzle
      const h = heldGun(family, magOut, parts), flip = Math.cos(rig.angle) < 0 ? -1 : 1;
      g.translate(rig.grip.x * 2 * ratio, rig.grip.y * 2 * ratio); g.rotate(rig.angle); g.translate(h.muzzle.x - h.grip.x, flip * (h.muzzle.y - h.grip.y));
    } else { g.translate(rig.muzzle.x * 2 * ratio, rig.muzzle.y * 2 * ratio); g.rotate(rig.angle); }
    polygon(g, [[0, -1], [4, -5], [4, -2], [10, 0], [4, 2], [4, 5], [0, 1]], K.amber); px(g, 0, -1, 5, 2, K.white); g.restore();
  }
}

/** Full unarmed figure for the character menu and callers of the previous sprite API. */
export function personCanvas(l: Look, facing: Facing, frame: number): HTMLCanvasElement {
  const p: MotionPose = { facing, travel: facing, frame: Math.max(0, Math.min(8, frame)), idle: 0, bob: 0,
    aim: FACINGS.indexOf(facing) * Math.PI / 4, kind: 'none', recoil: 0, swing: -1, swingSide: 1, reload: -1,
    throwing: -1, vault: 0, charge: 0, down: false };
  const [c, g] = canvas(CHAR_W, CHAR_H); g.translate(CHAR_W / 2, CHAR_H); drawCharacterCanvas(g, l, p, rigPose(p)); return c;
}
